import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";

import {
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../../data/Axios/DynamicService";

import { HRService } from "../../../../config";
import Header from "../../../../components/Header";

import {
  PayComponent,
  AllCalculationType,
  AllPayrollBasedOnCalculationType,
} from "../../../../data/Queries/HRmanagement/leavemanagement";

const base_url = HRService.uri;

const StatutoryForm = (props) => {
  const [paycomponents, setPayComponents] = useState([]);
  const [calculationTypes, setCalculationTypes] = useState([]);
  const [basedOnTypes, setBasedOnTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const isNonMobile = useMediaQuery("(min-width:600px)");
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken?.Id);

  // -----------------------------
  // Initial Form Values
  // -----------------------------
  const initialValues = {
    //payComponentId: props?.data?.payComponentId || "",
    code: props?.data?.code || "",
    name: props?.data?.name || "",
    calculationType: props?.data?.calculationType || "",
    basedOn: props?.data?.basedOn || "",
    employeeDefaultRate: props?.data?.employeeDefaultRate || "",
    employerDefaultRate: props?.data?.employerDefaultRate || "",
    hasRelief: props?.data?.hasRelief || false,
    reliefType: props?.data?.reliefType || "",
    isActive: props?.data?.isActive ?? true,
  };

  // -----------------------------
  // Validation Schema
  // -----------------------------
  const validationSchema = yup.object().shape({
    //payComponentId: yup.string().required("Pay Component is required"),
    code: yup.string().required("Code is required"),
    name: yup.string().required("Name is required"),
    calculationType: yup.string().required("Calculation Type is required"),
    basedOn: yup.string().required("Based On is required"),
    employeeDefaultRate: yup
      .number()
      .typeError("Must be a number")
      .min(0, "Cannot be negative"),
    employerDefaultRate: yup
      .number()
      .typeError("Must be a number")
      .min(0, "Cannot be negative"),
    reliefType: yup.string().when("hasRelief", {
      is: true,
      then: (schema) => schema.required("Relief Type is required"),
    }),
  });

  // -----------------------------
  // Fetch Data
  // -----------------------------
  useEffect(() => {
    async function loadData() {
      try {
        const pay = await graphqlQuery(PayComponent, base_url);
        const calc = await graphqlQuery(AllCalculationType, base_url);
        const based = await graphqlQuery(AllPayrollBasedOnCalculationType, base_url);

        setPayComponents(pay || []);
        setCalculationTypes(calc || []);
        setBasedOnTypes(based || []);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // -----------------------------
  // Options Mapped for Selects
  // -----------------------------
  const payComponentOptions = paycomponents
    .filter((p) => p.isStatutory)
    .map((p) => ({ value: p.name, label: p.name }));

  const calculationTypeOptions = calculationTypes.map((c) => ({
    value: c.name,
    label: c.name,
  }));

  const basedOnOptions = basedOnTypes.map((b) => ({
    value: b.name,
    label: b.name,
  }));

  // -----------------------------
  // Form Submit Handler
  // -----------------------------
  const handleFormSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        ...values,
        employeeDefaultRate: parseFloat(values.employeeDefaultRate) || 0,
        employerDefaultRate: parseFloat(values.employerDefaultRate) || 0,
        //[props.isEditing ? "updatedBy" : "createdBy"]: userId,
      };

      const mutationName = props.isEditing
        ? "updateStatutoryComponent"
        : "createStatutoryComponent";

      if (props.isEditing) payload.id = props.data?.id;

      const response = await generateAndExecuteMutation(
        mutationName,
        payload,
        "statutoryComponent",
        base_url
      );

      if (response) {
        props.onAction();
        props.onClose();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Component ${props.isEditing ? "updated" : "created"} successfully.`,
        });
      }
    } catch (error) {
      console.error("Submit Error:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Something went wrong. Try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // -----------------------------
  // Loading State
  // -----------------------------
  if (loading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    );

  // -----------------------------
  // UI Rendering
  // -----------------------------
  return (
    <Box m="20px">
      <Header
        title={props.isEditing ? "Edit Statutory Component" : "Create Statutory Component"}
        subtitle={
          props.isEditing
            ? "Modify details of the statutory component"
            : "Add a new statutory component"
        }
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          setFieldValue,
          handleSubmit: formikSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={formikSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{ "& > div": { gridColumn: isNonMobile ? undefined : "span 4" } }}
            >
              {/* Pay Component */}
              {/* <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Pay Component *</InputLabel>
                <Select
                  name="payComponentId"
                  value={values.payComponentId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.payComponentId && !!errors.payComponentId}
                >
                  <MenuItem value="" disabled>
                    Select Pay Component
                  </MenuItem>
                  {payComponentOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
                {touched.payComponentId && (
                  <p style={{ color: "red", fontSize: 12 }}>{errors.payComponentId}</p>
                )}
              </FormControl> */}

              {/* Code */}
              <TextField
                fullWidth
                variant="filled"
                label="Code *"
                name="code"
                value={values.code}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.code && !!errors.code}
                helperText={touched.code && errors.code}
                sx={{ gridColumn: "span 2" }}
              />

              {/* Name */}
              <TextField
                fullWidth
                variant="filled"
                label="Name *"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                sx={{ gridColumn: "span 2" }}
              />

              {/* Calculation Type */}
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Calculation Type *</InputLabel>
                <Select
                  name="calculationType"
                  value={values.calculationType}
                  onChange={handleChange}
                  error={touched.calculationType && !!errors.calculationType}
                >
                  <MenuItem value="" disabled>
                    Select Type
                  </MenuItem>
                  {calculationTypeOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Based On */}
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Based On *</InputLabel>
                <Select
                  name="basedOn"
                  value={values.basedOn}
                  onChange={handleChange}
                  error={touched.basedOn && !!errors.basedOn}
                >
                  <MenuItem value="" disabled>
                    Select Based On
                  </MenuItem>
                  {basedOnOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Employee Default Rate */}
              <TextField
                fullWidth
                variant="filled"
                type="number"
                inputProps={{ step: "any" }}
                label="Employee Default Rate"
                name="employeeDefaultRate"
                value={values.employeeDefaultRate}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.employeeDefaultRate && !!errors.employeeDefaultRate}
                helperText={touched.employeeDefaultRate && errors.employeeDefaultRate}
                sx={{ gridColumn: "span 2" }}
              />

              {/* Employer Default Rate */}
              <TextField
                fullWidth
                variant="filled"
                type="number"
                inputProps={{ step: "any" }}
                label="Employer Default Rate"
                name="employerDefaultRate"
                value={values.employerDefaultRate}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.employerDefaultRate && !!errors.employerDefaultRate}
                helperText={touched.employerDefaultRate && errors.employerDefaultRate}
                sx={{ gridColumn: "span 2" }}
              />

              {/* Has Relief */}
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                <InputLabel>Has Relief</InputLabel>
                <Select
                  value={values.hasRelief ? "Yes" : "No"}
                  onChange={(e) => setFieldValue("hasRelief", e.target.value === "Yes")}
                >
                  <MenuItem value="No">No</MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Submit Button */}
            <Box display="flex" justifyContent="flex-end" mt={3}>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default StatutoryForm;
